import { Injectable, Type, computed, inject, signal } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { ReplaySubject, distinctUntilChanged, filter, of, switchMap, takeUntil, tap } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { ModalErrorComponent, ModalErrorData } from 'app/shared/modal-error/modal-error.component';
import { HelpCenterUrls } from 'app/utils/help-center';
import { Loggable } from 'app/utils/logging/loggable';
import { notUndefined } from 'app/utils/stream-util';
import Social from 'entity/Social';

import { ModalSimpleSocialComponent } from '../../modal-simple-social/modal-simple-social.component';
import { VideoEffect, VideoEffectsMode, socialAppSelectors } from '../../state/app';
import { SimpleMediaDeviceInfo, mediaSelectors } from '../../state/media';
import { CAMERA_UNREADABLE_HELP_TEXT, CAMERA_UNREADABLE_TEXT, CAMERA_UNREADABLE_TITLE } from '../../strings';
import { BrowserService } from '../browser.service';
import { DeviceService } from '../device/device.service';
import { doesBrowserSupportVideoEffects } from '../device/utils';
import { MultimediaUserService } from '../multimedia-user.service';
import DevicePermissionStatus, { PermissionsService } from '../permissions.service';
import { VideoEffectService } from '../video-effect.service';

import { ModalSimpleMessageService } from './../../../../shared/modals/modal-simple-message.service';

@Injectable({ providedIn: 'root' })
export class DevicePermissionService extends Loggable {
  private store = inject(Store);
  public dialog = inject(MatDialog);
  private modalSimpleMessageService = inject(ModalSimpleMessageService);
  private deviceService = inject(DeviceService);
  private multimediaUserService = inject(MultimediaUserService);
  private videoEffectService = inject(VideoEffectService);
  private permissionsService = inject(PermissionsService);
  private browserService = inject(BrowserService);

  private destroyed$ = new ReplaySubject<boolean>(1);
  public readonly permission = signal<DevicePermissionStatus>(new DevicePermissionStatus());
  public readonly social = signal<Social | undefined>(undefined);

  // Devices variables as signals
  public readonly cameras = signal<SimpleMediaDeviceInfo[]>([]);
  public readonly mics = signal<SimpleMediaDeviceInfo[]>([]);
  public readonly speakers = signal<SimpleMediaDeviceInfo[]>([]);

  public readonly selectedCamera = signal<SimpleMediaDeviceInfo | null>(null);
  public readonly selectedMic = signal<SimpleMediaDeviceInfo | null>(null);
  public readonly selectedSpeaker = signal<SimpleMediaDeviceInfo | null>(null);

  public readonly isVideoReady = signal<boolean>(false);
  public readonly isMicReady = signal<boolean>(false);
  public readonly isAudioReady = signal<boolean>(false);

  public readonly isVideoEnabled = signal<boolean>(true);
  public readonly isMicEnabled = signal<boolean>(true);
  public readonly areVideoEffectsEnabled = signal<boolean>(false);

  // Customer API Settings
  public readonly setGuestVideo = signal<boolean>(false);
  private readonly muteGuestVideo = signal<boolean>(false);
  private readonly setGuestAudio = signal<boolean>(false);
  private readonly muteGuestAudio = signal<boolean>(false);

  public readonly isCameraUnreadable = signal<boolean>(false);
  private readonly isTestUser = signal<boolean>(false);

  public readonly ready = signal<boolean>(true);
  public readonly shouldShow = signal<boolean>(true);
  public readonly isHost = signal<boolean>(false);

  // Modals
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private dialogRef: any;

  public readonly isModalOpen = signal<boolean>(false);

  /**
   * Returns an Observable that emits whenever the canUserJoin state changes
   * // currently unused, remove?
   */
  public canUserJoinChanges$ = new ReplaySubject<boolean>(1);

  /**
   * Updates the canUserJoin state and notifies subscribers
   */
  private updateCanUserJoinState(): void {
    this.canUserJoinChanges$.next(this.canUserJoin());
  }

  private openModal<T, D>(component: Type<T>, config: MatDialogConfig<D>) {
    this.isModalOpen.set(true);
    const dialogRef = this.dialog.open(component, config);

    dialogRef
      .afterClosed()
      .pipe(
        tap(() => this.isModalOpen.set(false)),
        takeUntil(this.destroyed$),
      )
      .subscribe();

    return dialogRef;
  }

  public constructor() {
    super();

    // Initialize with current state
    this.updateCanUserJoinState();

    this.store
      .select(coreSelectors.isTestUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isTestUser) => this.isTestUser.set(isTestUser));

    this.store
      .select(socialAppSelectors.isGuestCameraStateApplied)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((state) => {
        this.setGuestVideo.set(state);
        this.updateCanUserJoinState();
      });

    this.store
      .select(socialAppSelectors.muteGuestCamera)
      .pipe(filter(notUndefined), takeUntil(this.destroyed$))
      .subscribe((setting) => {
        this.muteGuestVideo.set(setting);
        this.updateCanUserJoinState();
      });

    this.store
      .select(socialAppSelectors.isGuestMicStateApplied)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((state) => {
        this.setGuestAudio.set(state);
        this.updateCanUserJoinState();
      });

    this.store
      .select(socialAppSelectors.muteGuestMic)
      .pipe(filter(notUndefined), takeUntil(this.destroyed$))
      .subscribe((setting) => {
        this.muteGuestAudio.set(setting);
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.video_background_blur)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((areEffectsEnabled) =>
        this.areVideoEffectsEnabled.set(
          areEffectsEnabled &&
            doesBrowserSupportVideoEffects(this.browserService.browserType) &&
            !this.browserService.isAndroidDevice(),
        ),
      );

    this.store
      .select(socialAppSelectors.isGuestBlurEffectApplied)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((applyBlur) => {
        if (applyBlur && this.areVideoEffectsEnabled()) {
          const effect: VideoEffect = { enabled: true, mode: VideoEffectsMode.BLUR, blurRadius: 3 };
          this.videoEffectService.applyEffect(effect);
        }
      });

    this.store
      .select(mediaSelectors.isMicEnabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled) => this.isMicEnabled.set(enabled));

    this.store
      .select(mediaSelectors.isVideoEnabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled) => this.isVideoEnabled.set(enabled));

    this.store
      .select(mediaSelectors.getCameras)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((cameras) => {
        this.cameras.set(cameras);
      });

    this.store
      .select(mediaSelectors.getSelectedCamera)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((camera) => {
        this.selectedCamera.set(camera);
        this.isVideoReady.set(false);
        this.updateCanUserJoinState();
      });

    this.store
      .select(mediaSelectors.getMics)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((mics) => this.mics.set(mics));

    this.store
      .select(mediaSelectors.getSelectedMic)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((mic) => {
        this.selectedMic.set(mic);
        this.isMicReady.set(true);
        this.updateCanUserJoinState();
      });

    this.store
      .select(mediaSelectors.getSpeakers)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((speakers) => this.speakers.set(speakers));

    this.store
      .select(mediaSelectors.getSelectedSpeaker)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((speaker) => {
        this.selectedSpeaker.set(speaker);
        this.isAudioReady.set(!!speaker);
        this.updateCanUserJoinState();
      });

    this.store
      .select(mediaSelectors.isCurrentCameraReadable)
      .pipe(
        distinctUntilChanged(),
        tap((readable) => this.isCameraUnreadable.set(!readable)),
        switchMap((readable) => {
          if (readable) {
            return of(undefined);
          }
          const url = HelpCenterUrls.SCOOT_ENABLE_CAMERA_AND_MIC;
          const data: ModalErrorData = {
            title: CAMERA_UNREADABLE_TITLE,
            text: CAMERA_UNREADABLE_TEXT,
            helpLink: { url: url, text: CAMERA_UNREADABLE_HELP_TEXT },
          };

          return this.dialog.open<ModalErrorComponent, ModalErrorData>(ModalErrorComponent, { data }).afterClosed();
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();
  }

  /**
   *  PERMISSIONS HANDLING
   */
  public async initPermissions(): Promise<void> {
    try {
      this.trace('Initializing Permissions');
      const permission = await this.permissionsService.getPermissionStatus();
      this.info('Got Permissions with Status: ', permission);
      this.permissionsService.didUpdatePermission
        .pipe(
          switchMap((perm) => this.handlePermissions(perm)),
          takeUntil(this.destroyed$),
        )
        .subscribe();
      await this.handlePermissions(permission);
    } catch (error) {
      this.error('Error Initializing Permissions', error);
    }
  }

  public async handlePermissions(permission: DevicePermissionStatus): Promise<void> {
    this.permission.set(permission);

    // Check Customer API Video Settings
    if (this.setGuestVideo() && this.muteGuestVideo()) {
      this.isVideoReady.set(true);
      this.setVideoState();
    }

    // Check Customer API Mic Settings
    if (this.setGuestAudio() && this.muteGuestAudio()) {
      this.isAudioReady.set(true);
      this.setAudioState();
    }

    await this.loadAndSetDevices(permission.audioGranted(), permission.videoGranted());
    this.updateCanUserJoinState();
  }

  /* handle mic, speaker and camera devices */
  public async loadAndSetDevices(audioGranted: boolean, videoGranted: boolean): Promise<void> {
    if (!audioGranted && !videoGranted) {
      return;
    }

    try {
      await this.deviceService.initializeDevices({ audio: audioGranted, video: videoGranted });
    } catch (error) {
      if (!this.isTestUser()) {
        this.error('Error Loading and Setting Devices ', error);
        this.showInitDeviceError();
        return;
      }
    }

    if (this.isTestUser()) {
      this.setDevicesReadyFlags(true);
      return;
    }

    if (!(this.selectedMic() && this.selectedSpeaker())) {
      this.setDevicesReadyFlags(false);
      this.showInitDeviceError();
    }
  }

  /**
   *  CAMERA HANDLING
   */
  public async setCamera(camera: SimpleMediaDeviceInfo): Promise<void> {
    this.isVideoReady.set(false);
    if (!camera) {
      this.info('Tried to set invalid camera');
      return;
    }
    await this.deviceService.changeCameraDevice(camera);

    if (!isEqual(this.selectedCamera(), camera)) {
      this.showDeviceError(camera.label);
    }
    this.updateCanUserJoinState();
  }

  /**
   *  MICROPHONE HANDLING
   */
  public async setMic(mic: SimpleMediaDeviceInfo): Promise<void> {
    this.isMicReady.set(false);
    if (!mic) {
      this.warn('Tried to set invalid mic');
      return;
    }

    await this.deviceService.changeMicDevice(mic);

    if (!isEqual(this.selectedMic(), mic)) {
      this.showDeviceError(mic.label);
      this.isMicReady.set(false);
    }
    this.updateCanUserJoinState();
  }

  /**
   *  SPEAKER HANDLING
   */
  public setSpeaker(speaker?: SimpleMediaDeviceInfo): void {
    if (!speaker) {
      this.warn('Tried to set invalid speaker');
      return;
    }
    this.info(`Setting speaker to ${speaker.label} with id ${speaker.deviceId}`);
    this.selectedSpeaker.set(speaker);
    const service = this.deviceService;
    service.changeSpeakerDevice(speaker);

    if (!isEqual(this.selectedSpeaker(), speaker)) {
      this.showDeviceError(speaker.label);
    }
    this.updateCanUserJoinState();
  }

  //** States and Flags */
  public onVideoPlaying = (): void => {
    this.isVideoReady.set(true);
    this.updateCanUserJoinState();
  };

  public onVideoTimeout = (): void => {
    this.showDeviceError(this.selectedCamera()?.label ?? 'unknown camera');
    this.updateCanUserJoinState();
  };

  public setVideoState(): void {
    this.multimediaUserService.toggleVideo();
    this.updateCanUserJoinState();
  }

  /**
   * Computed property to determine if the camera button should be disabled
   */
  public readonly shouldCameraButtonDisabled = computed(() => {
    return this.cameras().length === 0 || this.isCameraUnreadable();
  });

  /**
   * Computed property to determine if video effects should be disabled
   */
  public readonly shouldDisableVideoEffects = computed(() => {
    return !this.areVideoEffectsEnabled() || !this.isVideoEnabled() || !this.isVideoReady();
  });

  /**
   * Computed property to determine if the avatar picture should be shown
   */
  public readonly shouldShowAvatarPicture = computed(() => {
    return !this.isVideoEnabled() || !this.permission().videoGranted() || !this.isVideoReady();
  });

  /**
   * Computed property to determine if the avatar video should be shown
   */
  public readonly shouldShowAvatarVideo = computed(() => {
    return !(this.isVideoEnabled() && this.permission().videoGranted() && this.isVideoReady());
  });

  public setAudioState(): void {
    this.multimediaUserService.userToggleAudio();
    this.updateCanUserJoinState();
  }

  public setDevicesReadyFlags(ready: boolean): void {
    this.isMicReady.set(ready);
    this.isVideoReady.set(ready);
    this.updateCanUserJoinState();
  }

  /**
   * Computed properties for device states and permissions
   */

  // Computed property for determining if camera is disabled
  private readonly isVideoDisabled = computed(() => {
    if (this.cameras().length === 0 && this.permissionsService.hasCheckedDevices()) {
      return true;
    }

    if (this.permissionsService.isVideoPermissionDenied()) {
      return true;
    }

    if (this.isCameraUnreadable()) {
      return true;
    }

    return false;
  });

  // Computed property for video permission status
  public readonly isVideoPermissionDenied = computed(() => {
    return this.permissionsService.isVideoPermissionDenied();
  });

  // Computed property for audio permission status
  public readonly isAudioPermissionDenied = computed(() => {
    return this.permissionsService.isAudioPermissionDenied();
  });

  /**
   * Determine if the users video is ready to use, or is disabled in some way
   * A user may still join if they deny camera permissions, or if they have no camera
   */
  public readonly isVideoReadyOrOff = computed(() => {
    return this.isVideoDisabled() || this.isVideoReady();
  });

  // Computed property to determine if user can join
  public readonly canUserJoin = computed(() => {
    return (
      (this.isVideoReadyOrOff() || this.setGuestVideo()) &&
      this.isAudioReady() &&
      this.isMicReady() &&
      this.permission().audioGranted()
    );
  });

  /**
   * Checks if audio capability is ready based on speaker selection and permissions
   * Both a speaker and audio permissions are required
   */
  public readonly isSpeakerAvailable = computed(() => {
    return this.speakers().length > 0 && !!this.selectedSpeaker();
  });

  /**
   * Checks if video capability is available based on camera selection and permissions
   */
  public readonly isCameraAvailable = computed(() => {
    return this.cameras().length > 0 && !!this.selectedCamera() && !this.isCameraUnreadable();
  });

  /**
   * Checks if device has been properly initialized and is ready to use
   * @param type The type of device to check
   * @returns Whether the device is ready for use
   */
  public isDeviceReady(type: 'audio' | 'video' | 'mic'): boolean {
    switch (type) {
      case 'audio':
        return this.isAudioReady() && !!this.selectedSpeaker();
      case 'video':
        return this.isVideoReady() && !!this.selectedCamera() && !this.isCameraUnreadable();
      case 'mic':
        return this.isMicReady() && !!this.selectedMic();
      default:
        return false;
    }
  }

  /**
   * Checks if all required devices for joining are ready
   * This is a convenience method that uses signals internally
   * @returns Whether all required devices are ready
   */
  public areAllRequiredDevicesReady(): boolean {
    // Audio is always required
    const isAudioOk = this.isDeviceReady('audio');
    const isMicOk = this.isDeviceReady('mic');

    // Video can be optional in some cases
    const isVideoOk = this.isDeviceReady('video') || this.isVideoDisabled() || this.setGuestVideo();

    return isAudioOk && isMicOk && isVideoOk;
  }

  /* Modals */
  /**
   * Show error modal for device initialization failure
   * Offers reload option to retry device access
   */
  public showInitDeviceError(): void {
    const data = {
      modalTitle: 'Failed Initializing Devices',
      text: 'Looks like the browser was unable to access your devices. This usually happens when other applications are using your camera or mic. Please quit all other applications using your camera or mic and reload the page.',
      primaryButtonText: 'Reload',
    };

    const dialogRef = this.openModal(ModalSimpleSocialComponent, {
      panelClass: 'social-modal',
      data: data,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((_result) => {
        if (_result === 'primary') {
          void navigator.permissions
            .query({ name: 'microphone' as PermissionName })
            .then((micPermission) => {
              if (micPermission.state === 'granted') {
                window.location.reload();
              }
            })
            .catch((e) => {
              // Handle any errors that might come up when querying permissions
              this.error('Error mic permissions', e);
            });
        }
      });
  }

  /**
   * Show error modal for device access problems
   * @param deviceLabel The name of the device that failed
   */
  public showDeviceError(deviceLabel: string): void {
    const message: ModalErrorData = {
      title: 'Oops!',
      text: `The device ${deviceLabel} is not available. Please check if another application is using it.`,
    };
    this.openModal(ModalErrorComponent, { data: message });
  }

  /**
   * Computed property to generate the appropriate permission modal title and message
   * based on which permissions are blocked
   */
  private readonly permissionModalContent = computed(() => {
    const cameraAccess = this.permission().videoGranted();
    const micAccess = this.permission().audioGranted();

    let modalTitle = '';
    let message = '';

    if (!cameraAccess && !micAccess) {
      modalTitle = 'Microphone and camera are blocked';
      message = 'Please update your browser permissions to give Scoot access to your Microphone and Camera.';
    } else if (!cameraAccess) {
      modalTitle = 'Camera is blocked';
      message = 'Please update your browser permissions to give Scoot access to your Camera.';
    } else {
      modalTitle = 'Microphone is blocked';
      message = 'Please update your browser permissions to give Scoot access to your Microphone.';
    }

    return { modalTitle, message };
  });

  /**
   * Show explanation modal for missing device permissions
   * Provides help resources for enabling camera/microphone
   */
  public showExplanationModal(): void {
    if (this.isModalOpen()) {
      return;
    }

    const content = this.permissionModalContent();

    const data = {
      title: content.modalTitle,
      titleIcon: 'SWIRL_ARROW',
      message: content.message,
      showActionButton: false,
      secondaryButtonTitle: 'Dismiss',
      tertiaryButtonTitle: 'Get Help',
      tertiaryLink: HelpCenterUrls.SCOOT_ENABLE_CAMERA_AND_MIC,
      showSecondaryButton: true,
      showTertiaryButton: true,
      showClose: true,
      showDone: false,
    };

    this.isModalOpen.set(true);
    this.dialogRef = this.modalSimpleMessageService.throw({ ...data }, true, false, true);

    // Update modal state when closed
    if (this.dialogRef && this.dialogRef.afterClosed) {
      this.dialogRef
        .afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => this.isModalOpen.set(false));
    }
  }

  /**
   * Automatically sets the default devices if none are selected
   */
  public async ensureDefaultDevicesAreSet(): Promise<void> {
    this.info('Ensuring default devices are set');

    // Set default camera if needed
    if (this.cameras().length > 0 && !this.selectedCamera()) {
      const defaultCamera = this.cameras()[0];
      this.info('Setting default camera', defaultCamera);
      try {
        await this.setCamera(defaultCamera);
      } catch (error) {
        this.error('Error setting default camera', error);
      }
    }

    // Set default microphone if needed
    if (this.mics().length > 0 && !this.selectedMic()) {
      const defaultMic = this.mics()[0];
      this.info('Setting default microphone', defaultMic);
      try {
        await this.setMic(defaultMic);
      } catch (error) {
        this.error('Error setting default microphone', error);
      }
    }

    // Set default speaker if needed
    if (this.speakers().length > 0 && !this.selectedSpeaker()) {
      const defaultSpeaker = this.speakers()[0];
      this.info('Setting default speaker', defaultSpeaker);
      this.setSpeaker(defaultSpeaker);
    }

    this.updateCanUserJoinState();
  }

  /**
   * Releases all device resources
   * Call this when leaving a session or when devices need to be reset
   */
  public releaseDeviceResources(): void {
    this.info('Releasing all device resources');

    // Reset ready state signals
    this.isVideoReady.set(false);
    this.isAudioReady.set(false);
    this.isMicReady.set(false);

    // Update state to reflect that devices are no longer ready
    this.updateCanUserJoinState();
  }

  /**
   * Method to check current device permissions and update signals
   * This can be called whenever permissions might have changed
   */
  public async refreshDevicePermissions(): Promise<void> {
    try {
      const permissionStatus = await this.permissionsService.getPermissionStatus();
      this.permission.set(permissionStatus);

      // If permissions changed, we might need to reload devices
      if (permissionStatus.audioGranted() || permissionStatus.videoGranted()) {
        await this.loadAndSetDevices(permissionStatus.audioGranted(), permissionStatus.videoGranted());
      }

      this.updateCanUserJoinState();
    } catch (error) {
      this.error('Error refreshing device permissions', error);
    }
  }

  /**
   * Automatically applies user preferences or guest settings for devices
   * Uses signals to determine the appropriate state for audio/video
   */
  public applyDeviceSettings(): void {
    this.info('Applying device settings based on user preferences and room settings');

    // Apply video state based on guest settings if applicable
    if (this.setGuestVideo()) {
      const shouldMuteVideo = this.muteGuestVideo();
      this.info(`Applying guest video settings - muted: ${shouldMuteVideo}`);

      // Only change state if it differs from current state
      if (this.isVideoEnabled() !== !shouldMuteVideo) {
        this.setVideoState();
      }
    }

    // Apply audio state based on guest settings if applicable
    if (this.setGuestAudio()) {
      const shouldMuteAudio = this.muteGuestAudio();
      this.info(`Applying guest audio settings - muted: ${shouldMuteAudio}`);

      // Only change state if it differs from current state
      if (this.isMicEnabled() !== !shouldMuteAudio) {
        this.setAudioState();
      }
    }

    this.updateCanUserJoinState();
  }
}
